// frontend/src/components/NotificationManagement.js
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../axiosConfig';
import { Bell, Mail, Settings, BarChart3, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedCard, ThemedBadge } from './themed';
import { usePreferences } from '../contexts/PreferenceContext';
import { useScrollableTabs } from '../hooks/useScrollableTabs';
import { getNotificationChannelIcon } from '../utils/notificationChannelMetadata';
import notificationService from '../services/notificationService';
import { getTemplateAndStatisticsEligibility } from './notifications/notificationUtils';
import NotificationChannelConfig from './notifications/NotificationChannelConfig';
import NotificationTemplateManager from './notifications/NotificationTemplateManager';
import NotificationDeliveryLog from './notifications/NotificationDeliveryLog';
import NotificationEventTypeManager from './notifications/NotificationEventTypeManager';

/**
 * Notification management component for administrators
 * Provides admin interface for managing notification system
 */
const NotificationManagement = ({ userRole }) => {
    const { t } = useTranslation();
    const { background, text, border, cn } = useTheme();
    const { language } = usePreferences();
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('eventTypes'); // Default to first admin tab
    const [channelStatistics, setChannelStatistics] = useState({});
    const [visibilityData, setVisibilityData] = useState(null);
    const [entityChannelMap, setEntityChannelMap] = useState({}); // channel -> is_enabled
    const [manageableEventTypes, setManageableEventTypes] = useState([]); // Event types user can manage
    const [manageableChannels, setManageableChannels] = useState([]); // Channels user can manage regardless of entity toggle
    const [enabledTemplateEventTypes, setEnabledTemplateEventTypes] = useState([]); // Event types eligible for templates/statistics
    const [enabledTemplateChannels, setEnabledTemplateChannels] = useState([]); // Channels eligible for templates/statistics

    const entityId = localStorage.getItem('entityId');
    // const userRole = localStorage.getItem('role');
    const [adminPermissions, setAdminPermissions] = useState({
        canManageChannels: false,
        canManageTemplates: false,
        canViewStatistics: false,
        canManageEventTypes: false,
        channelsWithPermission: [] // List of channels user can manage
    });
    const tabScrollRefreshKey = [
        language,
        adminPermissions.canManageChannels,
        adminPermissions.canManageTemplates,
        adminPermissions.canViewStatistics,
        adminPermissions.canManageEventTypes
    ].join('|');
    const {
        tabsRef,
        canScrollLeft,
        canScrollRight,
        checkTabsScroll,
        scrollTabs
    } = useScrollableTabs({ refreshKey: tabScrollRefreshKey });


    useEffect(() => {
        if (entityId) {
            loadMenuVisibilityAndData();
        }
    }, [entityId, language, userRole]);

    // Load admin data when permissions change
    useEffect(() => {
        if (adminPermissions.canViewStatistics) {
            loadAdminData();
        }
    }, [adminPermissions.canViewStatistics, entityId, enabledTemplateChannels]);

    /**
     * Define available tabs based on admin permissions (no user preferences)
     */
    const tabs = useMemo(() => ([
        ...(adminPermissions.canManageEventTypes ? [
            {
                id: 'eventTypes',
                label: t('eventTypes'),
                icon: List,
                description: t('manageNotificationEventTypes')
            }
        ] : []),
        ...(adminPermissions.canManageChannels ? [
            {
                id: 'channels',
                label: t('channelConfiguration'),
                icon: Bell,
                description: t('configureNotificationChannels')
            }
        ] : []),
        ...(adminPermissions.canManageTemplates ? [
            {
                id: 'templates',
                label: t('templateManagement'),
                icon: Mail,
                description: t('customizeNotificationTemplates')
            }
        ] : []),
        ...(adminPermissions.canViewStatistics ? [
            {
                id: 'statistics',
                label: t('deliveryLog'),
                icon: BarChart3,
                description: t('viewDeliveryStatistics')
            }
        ] : [])
    ]), [adminPermissions, t]);

    // Auto-set first available tab as active if current tab is not available
    useEffect(() => {
        if (tabs.length > 0 && !tabs.find(tab => tab.id === activeTab)) {
            setActiveTab(tabs[0].id);
        }
    }, [tabs, activeTab]);

    /**
     * Load menu visibility data and notification settings
     */
    const loadMenuVisibilityAndData = async () => {
        try {
            setLoading(true);

            if (!entityId) {
                console.error('No entityId found');
                return;
            }

            // Get menu visibility data from the API (includes global status and role requirements)
            const visibility = await notificationService.checkMenuVisibility(entityId);
            setVisibilityData(visibility);

            // Get entity channel configuration to determine current enablement state
            const entityChannels = await notificationService.getEntityChannels(entityId);
            const entityMap = {};
            for (const channel of entityChannels) {
                entityMap[channel.channel] = channel.is_enabled === 1 || channel.is_enabled === true || channel.isEnabled === true;
            }
            setEntityChannelMap(entityMap);

            // Determine which event types and channels the user can manage based on roles + global activation
            const accessSummary = getTemplateAndStatisticsEligibility(visibility, entityChannels, userRole);
            console.log('Access Summary:', {
                manageableChannels: accessSummary.manageableChannels,
                enabledChannels: accessSummary.enabledChannels,
                hasChannelAccess: accessSummary.hasChannelAccess,
                entityChannels,
                visibility
            });
            setManageableEventTypes(accessSummary.manageableEventTypes);
            setManageableChannels(accessSummary.manageableChannels);
            setEnabledTemplateEventTypes(accessSummary.enabledEventTypes);
            setEnabledTemplateChannels(accessSummary.enabledChannels);

            setAdminPermissions({
                canManageChannels: accessSummary.hasChannelAccess,
                canManageTemplates: accessSummary.hasTemplateAccess,
                canViewStatistics: accessSummary.enabledEventTypes.length > 0 && accessSummary.enabledChannels.length > 0,
                canManageEventTypes: accessSummary.hasEventTypeAccess,
                channelsWithPermission: accessSummary.manageableChannels.map(channel => channel.channel)
            });

        } catch (error) {
            console.error('Failed to load menu visibility:', error);
            toast.error(t('errorLoadingNotificationSettings'));
        } finally {
            setLoading(false);
        }
    };


    /**
     * Load admin data (statistics) when permissions change
     */
    const loadAdminData = async () => {
        try {
            if (adminPermissions.canViewStatistics && entityId) {
                // Load channel statistics
                const statsRes = await axios.get(`/api/notification-settings/${entityId}/statistics`);
                if (statsRes.data.success) {
                    const permittedNames = new Set(enabledTemplateChannels.map(channel => channel.channel));
                    if (permittedNames.size === 0) {
                        setChannelStatistics({});
                        return;
                    }
                    const filteredStats = Object.fromEntries(
                        Object.entries(statsRes.data.data || {}).filter(([channelName]) => permittedNames.has(channelName))
                    );
                    setChannelStatistics(filteredStats);
                }
            }
        } catch (error) {
            // Non-critical error, statistics are optional
            console.warn('Failed to load admin statistics:', error);
        }
    };

    /**
     * Handle successful channel configuration update
     */
    const handleChannelsUpdate = () => {
        loadMenuVisibilityAndData();
        toast.success(t('channelConfigurationUpdated'));
    };

    // Show message if no notification channels are available
    const hasAnyAdminPermission = adminPermissions.canManageChannels || adminPermissions.canManageTemplates || adminPermissions.canViewStatistics || adminPermissions.canManageEventTypes;

    // Check if admin can manage anything (different from user notification availability)
    const canManageAnything = adminPermissions.canManageEventTypes || adminPermissions.canManageChannels;

    if (!loading && !canManageAnything) {
        return (
            <div className={cn("max-w-4xl mx-auto p-6", background.main)}>
                <ThemedCard className="p-8 text-center">
                    <Bell className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <h2 className={cn("text-xl font-semibold mb-2", text.primary)}>
                        {t('notificationManagementNotAvailable')}
                    </h2>
                    <p className={cn("text-sm mb-4", text.secondary)}>
                        {t('insufficientPermissionsOrNoGlobalSettings')}
                    </p>
                </ThemedCard>
            </div>
        );
    }

    // Loading state
    if (loading) {
        return (
            <div className={cn("flex justify-center items-center h-64", text.secondary)}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3">{t('loading')}...</span>
            </div>
        );
    }

    // No admin permissions
    if (!hasAnyAdminPermission) {
        return (
            <ThemedCard className="p-8">
                <div className={cn("text-center", text.secondary)}>
                    <Settings className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <h2 className={cn("text-xl font-semibold mb-2", text.primary)}>
                        {t('notificationManagementNotAvailable')}
                    </h2>
                    <p className="mb-4">
                        {t('insufficientPermissionsForNotificationManagement')}
                    </p>
                </div>
            </ThemedCard>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className={cn("text-2xl font-bold", text.primary)}>
                        {t('notificationManagement')}
                    </h1>
                    <p className={cn("text-sm mt-1", text.secondary)}>
                        {t('manageOrganizationNotificationSettings')}
                    </p>
                </div>

                {/* Available channels badges */}
                <div className="flex items-center gap-3 flex-wrap">
                    <span className={cn("text-sm font-medium", text.secondary)}>
                        {t('availableChannels')}:
                    </span>
                    {Object.entries(visibilityData?.globalChannels || {})
                        .map(([channel, isGloballyActive]) => {
                            // Check status for this channel
                            const isEnabled = entityChannelMap[channel] === true && isGloballyActive;
                            const Icon = getNotificationChannelIcon(channel);
                            const stats = channelStatistics[channel];
                            const hasPermission = adminPermissions.channelsWithPermission.includes(channel);

                            // Only show if globally active
                            if (!isGloballyActive) return null;
                            // If user lacks required role, hide channels that aren't enabled
                            if (!isEnabled && !hasPermission) return null;

                            // Determine status text
                            let statusText = '';
                            if (isEnabled) {
                                statusText = t('channelEnabled');
                            } else if (!isEnabled && hasPermission) {
                                statusText = t('globallyActiveNotEnabled');
                            } else {
                                statusText = t('channelDisabled');
                            }

                            return (
                                <ThemedBadge
                                    key={channel}
                                    variant={isEnabled ? "success" : (hasPermission ? "warning" : "secondary")}
                                    className={cn(
                                        "flex items-center space-x-1",
                                        !isEnabled && !hasPermission && "opacity-50"
                                    )}
                                >
                                    <Icon className="w-3 h-3" />
                                    <span title={statusText}>{t(channel)}</span>
                                    {isEnabled && stats && (
                                        <span className="ml-1 text-xs opacity-75">
                                            ({stats.successRate}%)
                                        </span>
                                    )}
                                    {/* Tooltip instead of inline description */}
                                </ThemedBadge>
                            );
                        })
                    }
                </div>
            </div>

            {/* Tabs Navigation */}
            <div className={cn("border-b relative", border.primary)}>
                {/* Left scroll indicator */}
                {canScrollLeft && (
                    <button
                        onClick={() => scrollTabs('left')}
                        className={cn(
                            "absolute left-0 top-0 bottom-0 z-10 px-1 flex items-center",
                            "bg-gradient-to-r from-white via-white to-transparent dark:from-gray-900 dark:via-gray-900"
                        )}
                        aria-label="Scroll tabs left"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                )}

                {/* Right scroll indicator */}
                {canScrollRight && (
                    <button
                        onClick={() => scrollTabs('right')}
                        className={cn(
                            "absolute right-0 top-0 bottom-0 z-10 px-1 flex items-center",
                            "bg-gradient-to-l from-white via-white to-transparent dark:from-gray-900 dark:via-gray-900"
                        )}
                        aria-label="Scroll tabs right"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                )}

                <nav
                    ref={tabsRef}
                    onScroll={checkTabsScroll}
                    className="flex space-x-8 overflow-x-auto scrollbar-thin"
                >
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={cn(
                                    "flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0",
                                    activeTab === tab.id
                                        ? "border-blue-500 text-blue-600 dark:text-blue-400"
                                        : cn(
                                            "border-transparent",
                                            text.secondary,
                                            "hover:text-gray-700 hover:border-gray-300 dark:hover:text-gray-300"
                                        )
                                )}
                                title={tab.description}
                            >
                                <Icon className="w-4 h-4 mr-2" />
                                {tab.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Tab Content */}
            <div className="min-h-96">
                {activeTab === 'eventTypes' && adminPermissions.canManageEventTypes && (
                    <NotificationEventTypeManager
                        entityId={entityId}
                        manageableEventTypes={manageableEventTypes}
                        userRole={userRole}
                        onEventTypesUpdate={loadMenuVisibilityAndData}
                    />
                )}

                {activeTab === 'channels' && adminPermissions.canManageChannels && (
                    <NotificationChannelConfig
                        entityId={entityId}
                        onChannelsUpdate={handleChannelsUpdate}
                        manageableChannels={manageableChannels}
                    />
                )}

                {activeTab === 'templates' && adminPermissions.canManageTemplates && (
                    <NotificationTemplateManager
                        entityId={entityId}
                        eventTypes={enabledTemplateEventTypes}
                        manageableChannels={enabledTemplateChannels}
                        userRole={userRole}
                    />
                )}

                {activeTab === 'statistics' && adminPermissions.canViewStatistics && (
                    <NotificationDeliveryLog
                        entityId={entityId}
                        channelStatistics={channelStatistics}
                        manageableChannels={manageableChannels.filter(ch => ch.is_enabled)}
                    />
                )}
            </div>

            {/* Footer info */}
            <ThemedCard className="p-4 mt-8">
                <div className={cn("text-sm", text.secondary)}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <Settings className="w-4 h-4 mr-2" />
                            <span>{t('notificationManagementStatus')}: </span>
                            <ThemedBadge variant="success" className="ml-1">
                                {t('active')}
                            </ThemedBadge>
                        </div>
                        <div className="flex items-center space-x-4">
                            <span>{t('eventTypes')}: {enabledTemplateEventTypes.length}/{manageableEventTypes.length}</span>
                            <span>{t('channels')}: {manageableChannels.filter(ch => ch.is_enabled).length}/{manageableChannels.length}</span>
                        </div>
                    </div>
                </div>
            </ThemedCard>
        </div>
    );
};

export default NotificationManagement;
